import { memo, useEffect, useState } from "react";

import useInboxDraftStore from "common/hooks/useInboxDraftStore";

import useConversationMessages from "../../datahooks/useConversationMessages";
import useConversationUpdates from "../../datahooks/useConversationUpdates";
import useFileUploadStore from "../../stores/useFileUploadStore";
import { ConversationUrnProvider } from "./ConversationUrnContext";
import InboxHeader from "./InboxHeader";
import MessageContainer from "./MessageField/MessageContainer";
import MessageFieldLoader from "./MessageField/MessageFieldLoader";
import MessageThread from "./MessageThread";
import NotificationArea from "./NotificationArea";

import type { Conversation } from "../../types";

interface ConversationContainerProps {
  activeConversation: Conversation;
  isLoadingConversations: boolean;
  resetActiveConversation?: () => void;
  showLeadPreview: (leadId: string) => void;
}

function ConversationContainer({
  activeConversation,
  isLoadingConversations,
  resetActiveConversation = null,
  showLeadPreview,
}: ConversationContainerProps) {
  const {
    conversation_urn: conversationUrn,
    unread_count: unreadCount,
    owner: accountId,
    last_message: lastMessage,
  } = activeConversation;

  const { messages, isLoadingMessages, loadNextMessages, hasMoreMessages } =
    useConversationMessages({
      accountId,
      conversationUrn,
      unreadCount,
      lastMessageCreatedAt: lastMessage?.created_at,
    });

  let hasMessageFromAnotherUser = false;

  // Determine if the conversation has any messages from the other participant
  if (messages) {
    hasMessageFromAnotherUser = messages.some(
      ({ sender }) => sender.id !== accountId,
    );
  }

  const draftKey = `${conversationUrn}:${accountId}`;

  const [editMessage, setEditMessage] = useState(null);
  const { markAsRead } = useConversationUpdates();

  useEffect(() => {
    if (activeConversation?.unread_count) {
      markAsRead({
        accountId: activeConversation.owner,
        conversationUrn: activeConversation.conversation_urn,
      });
    }
    setEditMessage(null);
  }, [activeConversation.conversation_urn]);

  const { removeDraft } = useInboxDraftStore();
  const { clearConversationFiles } = useFileUploadStore();

  return (
    <ConversationUrnProvider value={conversationUrn}>
      <InboxHeader
        conversation={activeConversation}
        isLoading={isLoadingConversations}
        resetActiveConversation={resetActiveConversation}
        showLeadPreview={showLeadPreview}
      />

      <MessageThread
        conversation={activeConversation}
        setEditMessage={setEditMessage}
        messages={messages}
        isLoadingMessages={isLoadingMessages}
        loadNextMessages={loadNextMessages}
        hasMoreMessages={hasMoreMessages}
        showLeadPreview={showLeadPreview}
      />

      {isLoadingConversations && !activeConversation ? (
        <MessageFieldLoader />
      ) : (
        <>
          <NotificationArea
            conversation={activeConversation}
            hasMessageFromAnotherUser={hasMessageFromAnotherUser}
          />

          <MessageContainer
            conversation={activeConversation}
            editMessage={editMessage}
            hasMessageFromAnotherUser={hasMessageFromAnotherUser}
            cancelEdit={() => {
              setEditMessage(null);
              removeDraft(draftKey);
              clearConversationFiles(draftKey);
            }}
          />
        </>
      )}
    </ConversationUrnProvider>
  );
}

export default memo(ConversationContainer);
